import React, { useEffect } from "react";

import { isEqual } from "lodash";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { usePrevious } from "@mantine/hooks";
import { Flyout } from "@shared/flyouts";

import { AnnotationForm } from "./annotation-form";
import { AnnotationList } from "./annotation-list/annotation-list";
import { useAnnotationForm } from "./hooks/use-annotation-form";
import { useAnnotationParam } from "./hooks/use-annotation-param";

import { type Annotation } from ".";

type Args = {
  annotations: Annotation[];
  isAnnotationsLoading: boolean;
  fetchAnnotations: (arg: {
    variables: { startDate: string; endDate: string };
  }) => void;
  createAnnotation: (
    description: string,
    annotatedDate: string,
    endDate?: string,
    campaignType?: string,
  ) => Promise<any>;
  updateAnnotation: (
    id: string,
    description: string,
    annotatedDate?: string,
    endDate?: string,
    campaignType?: string,
  ) => Promise<any>;
  deleteAnnotation: (id: string) => Promise<any>;
  isMmm?: boolean;
};

export const AnnotationsFlyout = ({
  annotations,
  fetchAnnotations,
  isAnnotationsLoading,
  createAnnotation,
  updateAnnotation,
  deleteAnnotation,
  isMmm = false,
}: Args) => {
  const {
    closeFlyout,
    isFlyoutOpen,
    createAnnotationDateParam,
    setCreateAnnotationDateParam,
    removeFilteredDateRangeAnnotations,
    filteredAnnotationDateRangeParam,
  } = useAnnotationParam();
  const {
    newAnnotation,
    editAnnotation,
    currentAnnotation,
    clearCurrentAnnotation,
  } = useAnnotationForm(isMmm);
  const prettyLogEventPage = usePrettyLogEventPage();

  const close = () => {
    closeFlyout();
    clearCurrentAnnotation();
    removeFilteredDateRangeAnnotations();
  };

  useEffect(() => {
    if (!isFlyoutOpen) {
      clearCurrentAnnotation();
    }
  }, [isFlyoutOpen, clearCurrentAnnotation]);

  const prevCreateAnnotationDateParam = usePrevious(createAnnotationDateParam);
  useEffect(() => {
    if (!isEqual(prevCreateAnnotationDateParam, createAnnotationDateParam)) {
      newAnnotation(createAnnotationDateParam);
      setCreateAnnotationDateParam(undefined);
    }
  }, [
    createAnnotationDateParam,
    prevCreateAnnotationDateParam,
    newAnnotation,
    setCreateAnnotationDateParam,
  ]);

  const clickNewAnnotation = () => {
    newAnnotation(filteredAnnotationDateRangeParam?.startDate);
    prettyLogEventPage({
      domain: isMmm ? "MMM Annotations" : "Annotations",
      action: "onClick",
      additionalActionInfo: "New Note button",
    });
  };

  if (!isFlyoutOpen) {
    return null;
  }

  return (
    <Flyout
      open={isFlyoutOpen}
      direction="right"
      className="px-5"
      onClose={close}
      drawerWidth={500}
    >
      <div data-testid="annotation-flyout">
        {currentAnnotation ? (
          <AnnotationForm
            isMmm={isMmm}
            currentAnnotation={currentAnnotation}
            clearCurrentAnnotation={clearCurrentAnnotation}
            isAnnotationsLoading={isAnnotationsLoading}
            createAnnotation={createAnnotation}
            updateAnnotation={updateAnnotation}
            deleteAnnotation={deleteAnnotation}
          />
        ) : (
          <AnnotationList
            isMmm={isMmm}
            newAnnotation={clickNewAnnotation}
            editAnnotation={editAnnotation}
            annotations={annotations}
            isAnnotationsLoading={isAnnotationsLoading}
            fetchAnnotations={fetchAnnotations}
          />
        )}
      </div>
    </Flyout>
  );
};
